import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Button, Modal, Form, InputNumber, Select, Space, message, Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

const METRIC_OPTIONS = [
  { value: 'Last15MinsInBreak',       label: 'Last 15 mins in Break' },
  { value: 'Last15MinsInNonACD',      label: 'Last 15 mins in Non-ACD' },
  { value: 'IB_PH_Handled',           label: 'IB PH Handled' },
  { value: 'IB_Chat_Handled',         label: 'IB Chat Handled' },
  { value: 'Refused_Qty',             label: 'Refused Qty' },
  { value: 'IB_Phone_Avg_Talk_Sec',   label: 'IB Avg Talk' },
  { value: 'IB_Phone_Avg_ACW_Sec',    label: 'IB Avg ACW' },
  { value: 'IB_Phone_Avg_HT_Sec',     label: 'IB Avg HT' },
  { value: 'IB_Phone_Max_ACW_Sec',    label: 'IB Max ACW' },
  { value: 'OB_To_Support',           label: 'OB To Support' },
  { value: 'OB_To_Tier2',             label: 'OB To Tier2' },
  { value: 'Qty_Holds',               label: 'Qty Holds' },
  { value: 'Total_Hold_Time_Sec',     label: 'Total Hold Time' },
  { value: 'OB_Leg1_Max_ACW_Sec',     label: 'OB L1 Max ACW' },
  { value: 'OB_Max_ACW_Sec',          label: 'OB Max ACW' },
  { value: 'IC Login to IC Available', label: 'IC Login to IC Available (mins)' },
  // Named for the comparison rather than the source, so the Max view can
  // adopt it when it grows a lunch column instead of needing its own row.
  { value: 'CCaaS Lunch vs Timecard Lunch', label: 'CCaaS Lunch vs Timecard Lunch (mins)' },
  { value: 'Punch to IC Available',   label: 'Punch to IC Available (mins)' },
  { value: 'Punch Out after IC Logout', label: 'Punch Out after IC Logout (mins)' },
  { value: 'Punch IC Gap',             label: 'Punch/IC Gap (mins)' },
];

const VALUE_TYPE_OPTIONS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'seconds', label: 'Seconds (MM:SS)' },
  { value: 'count',   label: 'Count' },
];

function fmtValue(v, type) {
  if (v == null) return '—';
  if (type === 'seconds') {
    const m = Math.floor(v / 60);
    const s = v % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  return String(v);
}

export default function DBALThresholdsAdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const [rows,    setRows]    = useState([]);
  const [teams,   setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/admin/dbal-thresholds')
      .then((r) => setRows(r.data))
      .catch(() => message.error('Failed to load thresholds.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/api/admin/teams').then((r) => setTeams(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditItem(record);
    form.setFieldsValue({
      metric:     record.Metric,
      max_value:  record.MaxValue,
      value_type: record.ValueType,
      team_id:    record.TeamID ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        metric:     values.metric,
        max_value:  values.max_value,
        value_type: values.value_type,
        team_id:    values.team_id ?? null,
      };
      if (editItem) {
        await api.put(`/api/admin/dbal-thresholds/${editItem.ID}`, body);
        message.success('Threshold updated.');
      } else {
        await api.post('/api/admin/dbal-thresholds', body);
        message.success('Threshold created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/dbal-thresholds/${id}`);
      message.success('Deleted.');
      load();
    } catch {
      message.error('Failed to delete.');
    }
  };

  const metricLabel = (key) => METRIC_OPTIONS.find((o) => o.value === key)?.label ?? key;

  const columns = [
    { title: 'Metric',      key: 'Metric',    render: (_, r) => metricLabel(r.Metric), ellipsis: true },
    { title: 'Team',        dataIndex: 'TeamName', key: 'TeamName', width: 180,
      render: (v) => v || <Typography.Text type="secondary">All Teams</Typography.Text> },
    { title: 'Max Value',   key: 'MaxValue',  width: 120, align: 'right',
      render: (_, r) => fmtValue(r.MaxValue, r.ValueType) },
    { title: 'Value Type',  dataIndex: 'ValueType', key: 'ValueType', width: 130,
      render: (v) => VALUE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v },
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 110,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete this threshold?" onConfirm={() => handleDelete(r.ID)} okText="Delete" okType="danger">
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>DBAL Other Stats Thresholds</Typography.Title>
        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Threshold</Button>}
      </div>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Configures red-flag thresholds for columns on the DBAL Max Other Stats by Day view.
        Team-specific entries override global ones.
      </Typography.Text>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No thresholds configured.' }}
      />

      <Modal maskClosable={false}
        title={editItem ? 'Edit Threshold' : 'Add Threshold'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editItem ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Metric" name="metric" rules={[{ required: true, message: 'Metric is required' }]}>
            <Select
              options={METRIC_OPTIONS}
              placeholder="Select a metric"
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item label="Value Type" name="value_type" initialValue="minutes" rules={[{ required: true }]}>
            <Select options={VALUE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item label="Max Value" name="max_value" rules={[{ required: true, message: 'Max value is required' }]}
            extra="For seconds, enter total seconds (e.g. 90 = 1:30). For minutes, enter whole minutes.">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Team" name="team_id"
            extra="Leave blank to apply to all teams; select a team to override for that team only">
            <Select
              allowClear
              placeholder="All Teams"
              options={teams.map((t) => ({ label: t.Name, value: t.ID }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
